<template>
    <div>
        <div class="container-fluid">
            <div class="flex flex-col">
                <div v-if="loading" role="status" aria-live="polite" class="w-full py-5 text-center text-theme-text">
                    {{ translations.integrations.common.loading[lang] }}...
                </div>
                <div v-if="noResults" role="status" aria-live="polite" class="w-full py-4 text-theme-text">
                    {{ translations.integrations[integrationSlug].errors['no-results'].message[lang] }}
                    {{ translations.integrations[integrationSlug].errors['no-results'].suggestion[lang] }}
                </div>
            </div>
        </div>
        <div v-show="!loading && !noResults">
            <div class="container-fluid">
                <div class="flex flex-col">
                    <div class="my-4 flex w-full items-center justify-center">
                        <div class="mx-0 flex">
                            <div class="flex items-center justify-between pr-0 text-theme-text">
                                <div>{{ sourceLangName }}</div>
                                <div class="px-3">
                                    <svg-icon
                                        icon="baseline-arrow_forward-24px"
                                        width="24px"
                                        height="24px"
                                        class="text-theme-toolbar-icon"
                                    />
                                </div>
                            </div>
                            <div class="LanguageList flex items-center">
                                <label for="supportedLanguages" class="sr-only">{{
                                    localizations[lang].tooltips.translate
                                }}</label>
                                <select
                                    id="supportedLanguages"
                                    v-model="targetLangCode"
                                    name="supportedLanguages"
                                    :aria-label="localizations[lang].tooltips.translate"
                                    class="w-full bg-transparent text-theme-text"
                                    @change="handle"
                                >
                                    <option
                                        v-for="language in supportedLanguages"
                                        :key="language.iso_639_code"
                                        :value="language.iso_639_code"
                                        :selected="language.iso_639_code === targetLangCode"
                                        class="truncate"
                                    >
                                        {{ language.name }}
                                    </option>
                                </select>
                                <svg-icon
                                    icon="ic_keyboard_arrow_down_white_24px"
                                    width="16px"
                                    height="16px"
                                    class="text-theme-toolbar-icon"
                                ></svg-icon>
                            </div>
                        </div>
                    </div>
                    <div class="w-full px-2">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <p
                            :lang="targetLangCode"
                            class="translated-text px-2 py-2 text-theme-text"
                            v-html="translatedText"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapState } from 'vuex';
import find from 'lodash/find';
import supportedLanguages from '_shared_/js/integrations/translate-supported-languages.js';
import ContentEncryptionService from '../../../../src/js/components/lib/ContentEncryptionService.js';
import Encrypter from '../../../../src/js/components/lib/Encrypter.js';
import Decrypter from '../../../../src/js/components/lib/Decrypter.js';
import { translateAPI } from '../../../../src/js/communication/integrations';

export default {
    name: 'Translate',
    props: {
        text: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            noResults: false,
            loading: true,
            sourceLangCode: '',
            targetLangCode: 'en',
            translatedText: '',
            supportedLanguages,
            integrationSlug: 'translate',
        };
    },
    computed: {
        ...mapState(['baseEndpoint', 'integrations', 'translations', 'lang', 'localizations']),
        sourceLangName() {
            const defaultName = this.translations.integrations[this.integrationSlug].languageNotDetected[this.lang];
            if (!this.sourceLangCode) return defaultName;

            if (this.localizations[this.lang][this.sourceLangCode]) {
                return this.localizations[this.lang][this.sourceLangCode];
            }

            return find(this.supportedLanguages, ['iso_639_code', this.sourceLangCode])?.name || defaultName;
        },
    },
    watch: {
        text: 'handle',
    },
    mounted() {
        this.handle();
    },
    methods: {
        async handle() {
            this.loading = true;
            try {
                const response = await translateAPI.translate({
                    text: ContentEncryptionService.shouldEncrypt() ? await Encrypter.encryptText(this.text) : this.text,
                    targetLang: this.targetLangCode,
                });

                if (response.translations.length === 0) {
                    this.loading = false;
                    this.noResults = true;
                    return;
                }

                this.sourceLangCode = response.translations[0].detectedSourceLanguage;
                this.translatedText = ContentEncryptionService.shouldDecrypt()
                    ? await Decrypter.decryptText(response.translations[0].translatedText)
                    : response.translations[0].translatedText;
            } catch (e) {
                console.error('error', e);
            }

            this.loading = false;

            this.preventSameLanguageTranslation();
        },
        preventSameLanguageTranslation() {
            if (this.sourceLangCode !== this.targetLangCode) return;

            if (this.sourceLangCode === 'es') {
                this.targetLangCode = 'en';
            } else {
                this.targetLangCode = 'es';
            }
            this.handle();
        },
    },
};
</script>

<style lang="scss" scoped>
.LanguageList {
    position: relative;

    select {
        appearance: none;
        border: none;
        border-radius: 0;
        color: var(--theme-text-color);
        border-bottom: 1px solid var(--theme-border-color);
        padding-right: 15px;
        background: transparent;
    }

    select option {
        background: var(--theme-background-reader);
        color: var(--theme-text-color);
    }

    select + div {
        position: absolute;
        right: 13px;
    }
}

.translated-text {
    border-radius: 3px;
}

:root.dark {
    .LanguageList select,
    .translated-text {
        color: var(--theme-text-color-dark, #ffffff);
        background-color: var(--theme-background-reader-dark, #1a202c);
    }

    .LanguageList select option {
        background-color: var(--theme-background-reader-dark, #1a202c);
    }
}
</style>
